// Turns raw key/mouse/wheel events into held actions and one-shot impulses, and builds the
// per-tick UserCmd from them. buildCmd() runs every tick, so it allocates nothing.

import { ACTIONS, buildLookup, type Action, type Keybinds } from './keybinds';
import { SELECT_DROP } from '../sim/weapons';
import {
  IN_ATTACK,
  IN_ATTACK2,
  IN_BACK,
  IN_DUCK,
  IN_FORWARD,
  IN_JUMP,
  IN_MOVELEFT,
  IN_MOVERIGHT,
  IN_RELOAD,
  IN_USE,
  type UserCmd,
} from '../sim/input';

/** A wheel notch holds a held action for this many ticks (like +jump on mwheel in the prototype). */
const WHEEL_PULSE_TICKS = 2;

const INDEX = Object.fromEntries(ACTIONS.map((a, i) => [a.id, i])) as Record<Action, number>;
const FORWARD = INDEX.forward;
const BACK = INDEX.back;
const LEFT = INDEX.moveleft;
const RIGHT = INDEX.moveright;
const JUMP = INDEX.jump;
const DUCK = INDEX.duck;
const RELOAD = INDEX.reload;
const USE = INDEX.use;
const ATTACK1 = INDEX.attack1;
const ATTACK2 = INDEX.attack2;

export class InputState {
  private lookup = new Map<string, Action>();
  private readonly down = new Set<string>();
  /** Per action: number of bound inputs currently held down. */
  private readonly count = new Int32Array(ACTIONS.length);
  /** Per action: ticks left of a wheel pulse. */
  private readonly pulse = new Int32Array(ACTIONS.length);

  /** Knife attacks wait here until the next tick's command picks them up. */
  private attackPending = 0;
  /** Weapon selects for the next ticks' commands, one per tick, in order (switch, drop, give...). */
  private readonly selects = new Uint8Array(8);
  private selectCount = 0;

  /** Called for one-shot actions (restart, inspect, attacks, debug) on press. */
  onImpulse: (a: Action) => void = () => {};

  setBinds(binds: Keybinds) {
    this.lookup = buildLookup(binds);
    this.clear();
  }

  actionFor(code: string): Action | undefined {
    return this.lookup.get(code);
  }

  /** Returns true if the code is bound. */
  press(code: string, repeat = false): boolean {
    const a = this.lookup.get(code);
    if (!a) return false;
    if (ACTIONS[INDEX[a]].held) {
      // A click shorter than a tick still reaches the next command.
      if (!repeat && a === 'attack1') this.attackPending |= IN_ATTACK;
      else if (!repeat && a === 'attack2') this.attackPending |= IN_ATTACK2;
      if (!this.down.has(code)) {
        this.down.add(code);
        this.count[INDEX[a]]++;
      }
    } else if (!repeat) this.impulse(a);
    return true;
  }

  release(code: string) {
    if (!this.down.delete(code)) return;
    const a = this.lookup.get(code);
    if (a && this.count[INDEX[a]] > 0) this.count[INDEX[a]]--;
  }

  /** Returns true if the wheel direction is bound. */
  wheel(code: 'WheelUp' | 'WheelDown'): boolean {
    const a = this.lookup.get(code);
    if (!a) return false;
    if (ACTIONS[INDEX[a]].held) this.pulse[INDEX[a]] = WHEEL_PULSE_TICKS;
    else this.impulse(a);
    return true;
  }

  private impulse(a: Action) {
    if (a === 'switchWeapon') this.queueWeapon(3);
    else if (a === 'drop') this.queueWeapon(SELECT_DROP);
    this.onImpulse(a);
  }

  held(a: Action): boolean {
    return this.isHeld(INDEX[a]);
  }

  private isHeld(i: number): boolean {
    return this.count[i] > 0 || this.pulse[i] > 0 || this.virtual[i] !== 0;
  }

  /** Actions held by on-screen controls (touch joysticks, auto jump), apart from real inputs. */
  private readonly virtual = new Uint8Array(ACTIONS.length);

  /** Hold or let go of an action from an on-screen control. */
  setVirtual(a: Action, on: boolean) {
    this.virtual[INDEX[a]] = on ? 1 : 0;
  }

  clear() {
    this.down.clear();
    this.count.fill(0);
    this.pulse.fill(0);
    this.virtual.fill(0);
    this.attackPending = 0;
    this.selectCount = 0;
  }

  /** A weapon select (UserCmd.weaponSelect) for a coming tick; `first` puts it ahead of the rest. */
  queueWeapon(code: number, first = false) {
    const q = this.selects;
    if (this.selectCount >= q.length) return;
    if (first) {
      q.copyWithin(1, 0, this.selectCount);
      q[0] = code;
    } else q[this.selectCount] = code;
    this.selectCount++;
  }

  /** Fill in the command for the next tick, then count down wheel pulses. */
  buildCmd(out: UserCmd, yaw: number, pitch: number, forwardspeed: number): UserCmd {
    const w = this.isHeld(FORWARD);
    const s = this.isHeld(BACK);
    const a = this.isHeld(LEFT);
    const d = this.isHeld(RIGHT);
    out.forwardmove = ((w ? 1 : 0) - (s ? 1 : 0)) * forwardspeed;
    out.sidemove = ((d ? 1 : 0) - (a ? 1 : 0)) * forwardspeed;
    out.buttons =
      (w ? IN_FORWARD : 0) |
      (s ? IN_BACK : 0) |
      (a ? IN_MOVELEFT : 0) |
      (d ? IN_MOVERIGHT : 0) |
      (this.isHeld(JUMP) ? IN_JUMP : 0) |
      (this.isHeld(DUCK) ? IN_DUCK : 0) |
      (this.isHeld(RELOAD) ? IN_RELOAD : 0) |
      (this.isHeld(ATTACK1) ? IN_ATTACK : 0) |
      (this.isHeld(ATTACK2) ? IN_ATTACK2 : 0) |
      (this.isHeld(USE) ? IN_USE : 0) |
      this.attackPending;
    this.attackPending = 0;
    out.weaponSelect = 0;
    if (this.selectCount > 0) {
      out.weaponSelect = this.selects[0];
      this.selects.copyWithin(0, 1, this.selectCount);
      this.selectCount--;
    }
    out.yaw = yaw;
    out.pitch = pitch;
    const pulse = this.pulse;
    for (let i = 0; i < pulse.length; i++) if (pulse[i] > 0) pulse[i]--;
    return out;
  }
}
